import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  AreaChart, Area, PieChart, Pie, Legend, CartesianGrid,
} from "recharts";
import {
  KanbanSquare, CheckCircle2, AlertTriangle, Users, TrendingUp, Clock, MessageSquare, Ticket, Link2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CATEGORY, PRIORITY, initials, timeAgo, fmtDate, TICKET_STATUS, MentionText } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { MyRequestsCard, OnLeaveTodayCard, PendingReimbursementsCard, ReimbursementSummaryCard } from "@/components/requests/DashboardCards";
import MyWorkCard from "@/components/MyWork";
import { UpcomingHolidaysCard, AnnouncementsCard } from "@/components/hr/HrBits";

const CAT_COLORS = { backlog: "#94a3b8", in_progress: "#f59e0b", in_review: "#0ea5e9", completed: "#10b981" };
const PRIO_COLORS = { urgent: "#ef4444", high: "#f97316", medium: "#f59e0b", low: "#94a3b8" };

function StatCard({ icon: Icon, label, value, tint, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 animate-fade-up" data-testid={testid}>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-5 h-5" /></div>
      <div>
        <p className="text-2xl font-display font-bold text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1.5 font-medium">{label}</p>
      </div>
    </div>
  );
}

export default function ManagerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [portals, setPortals] = useState([]);

  const load = async () => {
    const [a, n, tk, pr] = await Promise.all([
      api.get("/analytics/overview"),
      api.get("/notes/feed", { params: { limit: 20 } }),
      api.get("/tickets"),
      api.get("/portals"),
    ]);
    setData(a.data);
    setNotes(n.data);
    setTickets(tk.data);
    setPortals(pr.data);
  };
  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/notes/feed", { params: { limit: 20 } }).then((r) => setNotes(r.data)), 15000);
    return () => clearInterval(t);
  }, []);

  const catData = useMemo(() => data ? Object.entries(data.by_category).map(([k, v]) => ({ name: CATEGORY[k].label, key: k, value: v })) : [], [data]);
  const prioData = useMemo(() => data ? Object.entries(data.by_priority).map(([k, v]) => ({ name: PRIORITY[k].label, key: k, value: v })).filter((d) => d.value > 0) : [], [data]);

  if (!data) return <div className="p-8 text-slate-400">Loading analytics…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px]" data-testid="manager-analytics-dashboard">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Team Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Welcome back, {user?.name?.split(" ")[0]} — here's how delivery is tracking.</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={KanbanSquare} label="Total Tasks" value={data.totals.tasks} tint="bg-indigo-50 text-indigo-600" testid="stat-total-tasks" />
        <StatCard icon={CheckCircle2} label="Completed" value={data.totals.completed} tint="bg-emerald-50 text-emerald-600" testid="stat-completed" />
        <StatCard icon={AlertTriangle} label="Overdue" value={data.totals.overdue} tint="bg-red-50 text-red-600" testid="stat-overdue" />
        <StatCard icon={Ticket} label="Open Tickets" value={data.totals.open_tickets} tint="bg-sky-50 text-sky-600" testid="stat-open-tickets" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left column: charts + workload */}
        <div className="lg:col-span-8 space-y-6">
          {/* Completion trend */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              <h3 className="font-display text-base font-semibold text-slate-800">Completion trend (14 days)</h3>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.trend} margin={{ left: -20, right: 8, top: 4 }}>
                <defs>
                  <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gN" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4f46e5" stopOpacity={0.25} /><stop offset="100%" stopColor="#4f46e5" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} interval={1} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Area type="monotone" dataKey="created" name="Created" stroke="#4f46e5" fill="url(#gN)" strokeWidth={2} />
                <Area type="monotone" dataKey="completed" name="Completed" stroke="#10b981" fill="url(#gC)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tasks by status */}
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="manager-status-chart">
              <h3 className="font-display text-base font-semibold text-slate-800 mb-4">Tasks by status</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={catData} margin={{ left: -24, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {catData.map((e) => <Cell key={e.key} fill={CAT_COLORS[e.key]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Priority mix */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-display text-base font-semibold text-slate-800 mb-4">Priority mix</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={prioData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                    {prioData.map((e) => <Cell key={e.key} fill={PRIO_COLORS[e.key]} />)}
                  </Pie>
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Workload table */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="manager-workload-chart">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-indigo-600" />
              <h3 className="font-display text-base font-semibold text-slate-800">Per-engineer workload</h3>
            </div>
            <div className="space-y-3">
              {data.workload.map((w) => {
                const max = Math.max(...data.workload.map((x) => x.total), 1);
                return (
                  <div key={w.id} className="flex items-center gap-3" data-testid={`workload-row-${w.id}`}>
                    <Avatar className="w-8 h-8"><AvatarImage src={w.avatar} /><AvatarFallback className="bg-slate-100 text-slate-600 text-xs">{initials(w.name)}</AvatarFallback></Avatar>
                    <div className="w-32 shrink-0 text-sm font-medium text-slate-700 truncate">{w.name}</div>
                    <div className="flex-1"><Progress value={(w.total / max) * 100} className="h-2.5" /></div>
                    <div className="flex items-center gap-2 shrink-0 text-xs">
                      <span className="text-slate-500">{w.total} total</span>
                      <span className="text-emerald-600 font-medium">{w.completed} done</span>
                      {w.overdue > 0 && <span className="text-red-600 font-medium">{w.overdue} late</span>}
                    </div>
                  </div>
                );
              })}
              {data.workload.length === 0 && <p className="text-sm text-slate-400">No engineers yet.</p>}
            </div>
          </div>
        </div>

        {/* Right column: notes feed + overdue */}
        <div className="lg:col-span-4 space-y-6">
          <MyWorkCard />
          <AnnouncementsCard />
          <UpcomingHolidaysCard />
          <ReimbursementSummaryCard />
          {user?.is_hr && <PendingReimbursementsCard />}
          <OnLeaveTodayCard />
          <MyRequestsCard />
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="manager-latest-notes-feed">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-indigo-600" />
              <h3 className="font-display text-base font-semibold text-slate-800">Latest team notes</h3>
            </div>
            <div className="space-y-4 max-h-[460px] overflow-y-auto scroll-thin pr-1">
              {notes.map((n) => (
                <button key={n.id} onClick={() => navigate(`/boards/${n.board_id}?task=${n.task_id}`)}
                  className="w-full text-left group" data-testid={`feed-note-${n.id}`}>
                  <div className="flex items-start gap-2.5">
                    <Avatar className="w-7 h-7 mt-0.5"><AvatarImage src={n.author_avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-[10px]">{initials(n.author_name)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="font-semibold text-slate-800">{n.author_name}</span>
                        <span className="text-slate-400">· {timeAgo(n.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5 line-clamp-2 group-hover:text-slate-900"><MentionText text={n.content} /></p>
                      {n.mentions?.includes(user?.id) && (
                        <span className="inline-block mt-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5">mentioned you</span>
                      )}
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-slate-200 text-slate-500">{n.board_name}</Badge>
                        <span className="text-[11px] text-slate-400 truncate">{n.task_title}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              {notes.length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-red-500" />
              <h3 className="font-display text-base font-semibold text-slate-800">Overdue tasks</h3>
            </div>
            <div className="space-y-2.5 max-h-[300px] overflow-y-auto scroll-thin">
              {data.overdue_tasks.map((t) => (
                <button key={t.id} onClick={() => navigate(`/boards/${t.board_id}?task=${t.id}`)}
                  className="w-full text-left p-2.5 rounded-lg border border-red-100 bg-red-50/50 hover:bg-red-50 transition-colors" data-testid={`overdue-${t.id}`}>
                  <p className="text-sm font-medium text-slate-800 truncate">{t.title}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    <span className="text-red-600 font-medium">Due {fmtDate(t.due_date)}</span>
                    <span>· {t.assignee_name}</span>
                    <span>· {t.board_name}</span>
                  </div>
                </button>
              ))}
              {data.overdue_tasks.length === 0 && <p className="text-sm text-slate-400">Nothing overdue. 🎉</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="manager-recent-tickets">
            <div className="flex items-center gap-2 mb-4">
              <Ticket className="w-4 h-4 text-sky-500" />
              <h3 className="font-display text-base font-semibold text-slate-800">Recent tickets</h3>
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto scroll-thin">
              {tickets.slice(0, 7).map((t) => (
                <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50" data-testid={`dash-ticket-${t.id}`}>
                  <span className="font-mono text-[10px] text-slate-400 w-14 shrink-0">{t.key}</span>
                  <span className="flex-1 text-sm text-slate-700 truncate">{t.title}</span>
                  <Badge variant="outline" className={`h-4 px-1.5 text-[10px] shrink-0 ${TICKET_STATUS[t.status]?.chip}`}>{TICKET_STATUS[t.status]?.label}</Badge>
                </div>
              ))}
              {tickets.length === 0 && <p className="text-sm text-slate-400">No tickets yet.</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="manager-portals-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-indigo-600" />
                <h3 className="font-display text-base font-semibold text-slate-800">Client Portals</h3>
              </div>
              <button onClick={() => navigate("/portals")} className="text-xs font-medium text-indigo-600 hover:text-indigo-700" data-testid="portals-manage-link">Manage</button>
            </div>
            <div className="space-y-2 max-h-[240px] overflow-y-auto scroll-thin">
              {portals.slice(0, 6).map((p) => (
                <button key={p.id} onClick={() => navigate("/portals")} className="w-full text-left p-2 rounded-lg hover:bg-slate-50 flex items-center gap-2" data-testid={`dash-portal-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.label}</p>
                    <p className="text-[11px] text-slate-400 truncate">{p.client_name} · {p.url}</p>
                  </div>
                </button>
              ))}
              {portals.length === 0 && <p className="text-sm text-slate-400">No portals recorded. Add them in Client Portals.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
